import type { AppearanceModule } from '@setplex/tria-api'
import type { Route } from '~/core/router'

import { combine } from 'effector'
import { Slot } from '~/core/router'
import { NotificationToasts } from '~/entities/notification-toasts'
import { model as sessionModel } from '~/entities/session'
import { appearanceFeature } from '~/features/appearance'
import * as pinLockFeature from '~/features/pin-lock'
import { AccountSettingsModal } from '~/features/portal/redirect'
import { keys } from '~/shared/jail'
import { Footer } from '~/shared/ui/components/Footer'
import { NoContentBlock } from '~/shared/ui/components/NoContentBlock'
import { NavBar } from '~/widgets/NavBar'
import { ExternalPaymentOpen } from '~/widgets/Payment'
import {
  AnnouncementPopup,
  MessagePortal,
  ProfilesRemovePopup,
  VoiceSearchPopup,
} from '~/widgets/Popups'
import { SignInToWatchPopup } from '~/widgets/Popups/SignInToWatchPopup'
import * as catchup from './catchup'
import * as dynamic from './dynamic'
import * as epg from './epg'
import * as event from './event'
import * as home from './home'
import * as library from './library'
import * as live from './live'
import * as movie from './movie'
import * as no_content from './no-content'
import * as partner from './partner'
import * as search from './search'
import * as series from './series'
import * as settings from './settings'

const standardModule = {
  home: home.route,
  tv_channels: live.route,
  movie: movie.route,
  tv_shows: series.route,
  epg: epg.route,
  catchups: catchup.route,
  events: event.route,
}

export const appearanceRoutes: Route[] = []

const $noContentState = combine({
  modules: appearanceFeature.$modules,
  guestReloginPending: sessionModel.$guestReloginPending,
})

/**
 * Dynamically update routes based on available modules
 */
export function updateRoutes(modules: AppearanceModule[] | null) {
  // appearance is reloading, keep previous routes to avoid transient no-content
  if (modules === null) return

  // reset routes
  appearanceRoutes.length = 0

  // ready but empty, no routes to add
  if (modules.length === 0) return

  let shouldAddDynamic = false

  for (const mod of modules) {
    if (mod.identifier in standardModule && mod.type === 'static') {
      appearanceRoutes.push({
        path: `/${mod.identifier}`,
        load: standardModule[mod.identifier as keyof typeof standardModule],
        children: [], // this is REQUIRED for universal-router to work properly, see `impregnate` function description
      })
    } else if (mod.type === 'dynamic') {
      shouldAddDynamic = true
    }
  }

  if (shouldAddDynamic) {
    appearanceRoutes.push({
      path: `/:identifier`,
      load: dynamic.route,
      children: [], // this is REQUIRED for universal-router to work properly, see `impregnate` function description
    })
  }
}

export const route: Route = {
  keys: [keys.app, keys.guest],

  component: [
    NavBar,
    Slot,
    pinLockFeature.PinLockPopup,
    ExternalPaymentOpen,
    SignInToWatchPopup,
    NoContentBlock,
    ProfilesRemovePopup,
    VoiceSearchPopup,
    NotificationToasts,
    AnnouncementPopup,
    AccountSettingsModal,
    Footer,
    MessagePortal,
  ],

  children: [
    // standard subscriber-only modules (unavailable for guest)
    {
      keys: [keys.app],
      path: '/library',
      load: library.route,
    },
    {
      keys: [keys.app],
      path: '/settings',
      load: settings.route,
    },

    // search is available for guest
    {
      path: '/search',
      load: search.route,
    },
    {
      path: '/partner',
      load: partner.route,
    },

    {
      path: '{/*path}',
      filter: {
        source: $noContentState,
        fn: ({ modules, guestReloginPending }) =>
          !guestReloginPending &&
          modules?.length === 0 &&
          appearanceRoutes?.length === 0,
      },
      load: no_content.route,
    },

    // dynamically managed modules
    {
      children: appearanceRoutes,
    },

    // catch-all: redirect /modules or /modules/unknown to first module (MUST be last)
    {
      path: '{/*path}',
      action: () => ({ redirect: '/' }),
    },
  ],
}
